import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Row,
  Col,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { CopyOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import {
  createApiDocument,
  deleteApiDocument,
  getApiDocumentAuthConfig,
  getApiDocumentList,
  getApiDocumentVersions,
  getPublicApiDocumentVersions,
  getPublicApiDocuments,
  regenerateApiDocumentAuthConfig,
  updateApiDocument,
} from '../services/apiDocumentService.js';
import { useGlobalState } from '../hooks/useGlobalState.js';
import './styles/api-document-center.css';

const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;

const DEFAULT_FORM_VALUES = {
  name: '',
  version: 'v1',
  httpMethod: 'GET',
  path: '',
  summary: '',
  description: '',
  authType: 'Bearer Token',
  paramsJson: '[]',
  returnFieldsJson: '',
  requestExample: '',
  responseExample: '',
  errorCodesJson: '[]',
  changeLog: '',
};

const EXTERNAL_API_TOKEN_TTL_SECONDS = 28800; // 8 hours

const METHOD_COLORS = {
  GET: 'blue',
  POST: 'green',
  PUT: 'orange',
  PATCH: 'purple',
  DELETE: 'red',
};

const parseJsonText = (value) => {
  if (!value || !String(value).trim()) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const safeFormatTime = (value) => {
  if (!value) {
    return '-';
  }
  const time = dayjs(value);
  return time.isValid() ? time.format('YYYY-MM-DD HH:mm:ss') : '-';
};

const TOKEN_ENDPOINT_PATH = '/public/oauth2/token';
const SYSTEM_URLS = [
  {
    key: 'test',
    label: 'Dev System',
    url: 'https://api-dev.scams.report/',
  },
  {
    key: 'formal',
    label: 'Prod System',
    url: 'https://api.scams.report/',
  },
];

const copyText = async (text, label = 'Content') => {
  if (!text) {
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
    message.success(`${label} copied.`);
  } catch (error) {
    message.error(`Failed to copy ${label.toLowerCase()}.`);
  }
};

const normalizeDocTable = (value) => {
  const parsed = parseJsonText(value);
  if (!parsed) {
    return null;
  }
  if (Array.isArray(parsed)) {
    const headers = ['Field', 'Type', 'Description'];
    const rows = parsed.map((item, index) => ({
      key: `${item?.name || 'row'}-${index}`,
      Field: item?.name || '-',
      Type: item?.type || '-',
      Description: item?.description || '-',
    }));
    return { headers, rows };
  }
  if (parsed && typeof parsed === 'object') {
    const headers = Array.isArray(parsed.headers)
      ? parsed.headers.map((header) => String(header || '').trim()).filter(Boolean)
      : [];
    const rows = Array.isArray(parsed.rows)
      ? parsed.rows.map((row, index) => ({
        key: `row-${index}`,
        ...(row || {}),
      }))
      : [];
    if (headers.length > 0 || rows.length > 0 || parsed.note || parsed.text) {
      return {
        note: parsed.note || '',
        text: parsed.text || '',
        headers,
        rows,
      };
    }
  }
  return null;
};

const buildErrorRows = (errorCodesJson) => {
  const parsed = parseJsonText(errorCodesJson);
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed.map((item, index) => ({
    key: `${item?.code || 'error'}-${index}`,
    code: item?.code || '-',
    message: item?.message || '-',
    description: item?.description || '-',
  }));
};

const ApiDocumentCenter = () => {
  const [searchForm] = Form.useForm();
  const [documentForm] = Form.useForm();
  const { userProfile } = useGlobalState();
  const isCompanyUser = userProfile?.user?.userStatus === 'Company User';
  const currentCompanyId = userProfile?.user?.companyId ?? null;

  const [documents, setDocuments] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [versions, setVersions] = useState([]);
  const [publicVersions, setPublicVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDocuments, setPreviewDocuments] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [authConfig, setAuthConfig] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [docSearchTerm, setDocSearchTerm] = useState('');
  const [activeNavId, setActiveNavId] = useState('api-system-urls');

  const systemReferenceItems = useMemo(() => {
    const items = [
      { id: 'api-system-urls', label: 'System URLs', keywords: 'url api base test formal system' },
      { id: 'api-auth-guide', label: 'Authentication', keywords: 'authentication client secret token' },
      { id: 'api-token-response', label: 'Token Response', keywords: 'access token bearer expires response' },
      { id: 'api-use-token', label: 'Call API with token', keywords: 'authorization bearer call api token' },
      { id: 'api-token-lifetime', label: 'Token Lifetime & Refresh', keywords: 'token lifetime refresh expire auto renew' },
    ];

    if (currentCompanyId) {
      items.push({
        id: 'api-auth-credentials',
        label: 'Company API Credentials',
        keywords: 'company client id credentials regenerate',
      });
    }

    return items;
  }, [currentCompanyId]);

  const visibleSystemReferenceItems = useMemo(() => {
    const keyword = docSearchTerm.trim().toLowerCase();
    if (!keyword) {
      return systemReferenceItems;
    }

    return systemReferenceItems.filter((item) => (
      `${item.label} ${item.keywords}`.toLowerCase().includes(keyword)
    ));
  }, [docSearchTerm, systemReferenceItems]);

  const managementColumns = [
    {
      title: 'API',
      dataIndex: 'name',
      key: 'name',
      width: 260,
      render: (value, record) => (
        <div className="api-document-api-cell">
          <Text strong>{value}</Text>
        </div>
      ),
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 120,
      render: (value) => <Tag color="geekblue">{value}</Tag>,
    },
    {
      title: 'Method',
      dataIndex: 'httpMethod',
      key: 'httpMethod',
      width: 110,
      render: (value) => <Tag color={METHOD_COLORS[value] || 'default'}>{value}</Tag>,
    },
    {
      title: 'Path',
      dataIndex: 'path',
      key: 'path',
      width: 360,
      render: (value) => <Text code className="api-document-path-code">{value}</Text>,
    },
    {
      title: 'Auth',
      dataIndex: 'authType',
      key: 'authType',
      width: 150,
      render: (value) => value || '-',
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: safeFormatTime,
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 230,
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" onClick={() => handleEdit(record)}>Edit</Button>
          <Button type="link" danger onClick={() => handleDelete(record)}>Delete</Button>
        </Space>
      ),
    },
  ];

  const errorColumns = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 120 },
    { title: 'Message', dataIndex: 'message', key: 'message', width: 220 },
    { title: 'Description', dataIndex: 'description', key: 'description' },
  ];

  const previewRows = useMemo(() => previewDocuments || [], [previewDocuments]);

  const documentUpdatedAt = useMemo(() => {
    let latestTime = null;
    previewRows.forEach((document) => {
      const value = document?.updatedAt || document?.publishedAt;
      if (!value) {
        return;
      }
      const time = dayjs(value);
      if (time.isValid() && (!latestTime || time.isAfter(latestTime))) {
        latestTime = time;
      }
    });
    return latestTime ? latestTime.format('YYYY-MM-DD') : '-';
  }, [previewRows]);

  const systemPublicVersions = useMemo(() => (
    publicVersions.filter((version) => String(version || '').toLowerCase().includes('system'))
  ), [publicVersions]);

  const visibleEndpointReferenceItems = useMemo(() => {
    const keyword = docSearchTerm.trim().toLowerCase();
    if (!keyword) {
      return previewRows;
    }

    return previewRows.filter((document) => (
      `${document.name || ''} ${document.summary || ''} ${document.path || ''}`.toLowerCase().includes(keyword)
    ));
  }, [docSearchTerm, previewRows]);

  useEffect(() => {
    if (!isCompanyUser) {
      fetchManagementList(filters, pagination);
      fetchVersions();
    }
    fetchPublicVersions();
  }, []);

  useEffect(() => {
    if (selectedVersion) {
      fetchPreviewDocuments(selectedVersion);
    }
  }, [selectedVersion]);

  useEffect(() => {
    if (currentCompanyId) {
      fetchAuthConfig(currentCompanyId);
    }
  }, [currentCompanyId]);

  useEffect(() => {
    const allNavIds = [
      ...systemReferenceItems.map((item) => item.id),
      ...visibleEndpointReferenceItems.map((doc) => `api-document-${doc.id}`),
    ];

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const triggerPoint = windowHeight * 0.3;

      let currentId = allNavIds[0];
      for (let i = allNavIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(allNavIds[i]);
        if (el && el.getBoundingClientRect().top <= triggerPoint) {
          currentId = allNavIds[i];
          break;
        }
      }
      setActiveNavId(currentId);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [visibleEndpointReferenceItems]);

  const fetchVersions = async () => {
    try {
      const response = await getApiDocumentVersions();
      setVersions(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch API document versions:', error);
    }
  };

  const fetchPublicVersions = async () => {
    try {
      const response = await getPublicApiDocumentVersions();
      const versionList = response?.data || [];
      setPublicVersions(versionList);
      const systemVersions = versionList.filter((version) => (
        String(version || '').toLowerCase().includes('system')
      ));
      if (systemVersions.length === 0) {
        setSelectedVersion(undefined);
        setPreviewDocuments([]);
        return;
      }
      if (!selectedVersion || !systemVersions.includes(selectedVersion)) {
        setSelectedVersion(systemVersions[0]);
      }
    } catch (error) {
      console.error('Failed to fetch public API document versions:', error);
    }
  };

  const fetchAuthConfig = async (companyId) => {
    setAuthLoading(true);
    try {
      const response = await getApiDocumentAuthConfig(companyId);
      setAuthConfig(response?.data || null);
    } catch (error) {
      console.error('Failed to fetch external API auth config:', error);
      setAuthConfig(null);
    } finally {
      setAuthLoading(false);
    }
  };

  const fetchManagementList = async (params = filters, pager = pagination) => {
    setLoading(true);
    try {
      const response = await getApiDocumentList({
        ...params,
        pageNum: pager.current,
        pageSize: pager.pageSize,
      });
      setDocuments({
        rows: response?.rows || [],
        total: response?.total || 0,
      });
    } catch (error) {
      console.error('Failed to fetch API documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviewDocuments = async (version) => {
    setPreviewLoading(true);
    try {
      const response = await getPublicApiDocuments(version);
      setPreviewDocuments(response?.data?.documents || []);
    } catch (error) {
      console.error('Failed to fetch public API documents:', error);
      setPreviewDocuments([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSearch = (values) => {
    setFilters(values);
    const nextPagination = { ...pagination, current: 1 };
    setPagination(nextPagination);
    fetchManagementList(values, nextPagination);
  };

  const handleReset = () => {
    searchForm.resetFields();
    const nextFilters = {};
    const nextPagination = { current: 1, pageSize: 10 };
    setFilters(nextFilters);
    setPagination(nextPagination);
    fetchManagementList(nextFilters, nextPagination);
  };

  const handleTableChange = (pager) => {
    const nextPagination = {
      current: pager.current,
      pageSize: pager.pageSize,
    };
    setPagination(nextPagination);
    fetchManagementList(filters, nextPagination);
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    documentForm.setFieldsValue(DEFAULT_FORM_VALUES);
    setEditorOpen(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    documentForm.setFieldsValue({
      ...DEFAULT_FORM_VALUES,
      ...record,
      paramsJson: record?.paramsJson || '[]',
      returnFieldsJson: record?.returnFieldsJson || '',
      errorCodesJson: record?.errorCodesJson || '[]',
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingRecord(null);
    documentForm.resetFields();
  };

  const validateJsonField = (value, fieldName) => {
    if (!value || !String(value).trim()) {
      return;
    }
    try {
      JSON.parse(value);
    } catch (error) {
      throw new Error(`${fieldName} must be valid JSON.`);
    }
  };

  const handleSave = async () => {
    try {
      const values = await documentForm.validateFields();
      validateJsonField(values.paramsJson, 'Parameters JSON');
      validateJsonField(values.returnFieldsJson, 'Return Fields JSON');
      validateJsonField(values.errorCodesJson, 'Error Codes JSON');

      setEditorLoading(true);
      if (editingRecord?.id) {
        await updateApiDocument({
          ...editingRecord,
          ...values,
        });
        message.success('API document updated successfully.');
      } else {
        await createApiDocument(values);
        message.success('API document created successfully.');
      }
      closeEditor();
      fetchManagementList(filters, pagination);
      fetchVersions();
      fetchPublicVersions();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error(error.message || 'Failed to save API document.');
    } finally {
      setEditorLoading(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete API document',
      content: `Are you sure you want to delete "${record.name}"?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        await deleteApiDocument(record.id);
        message.success('API document deleted successfully.');
        fetchManagementList(filters, pagination);
        fetchVersions();
        fetchPublicVersions();
      },
    });
  };

  const handleRegenerateAuthConfig = async () => {
    if (!currentCompanyId) {
      message.error('Company context is required.');
      return;
    }
    try {
      setAuthLoading(true);
      const response = await regenerateApiDocumentAuthConfig(currentCompanyId);
      const payload = response?.data || null;
      setAuthConfig(payload);
      setGeneratedCredentials(payload);
      setCredentialsModalOpen(true);
      message.success('API credentials regenerated successfully.');
    } catch (error) {
      message.error(error?.message || 'Failed to regenerate API credentials.');
    } finally {
      setAuthLoading(false);
    }
  };

  const renderPreviewDocument = (document) => {
    const requestParameters = normalizeDocTable(document.paramsJson);
    const returnFields = normalizeDocTable(document.returnFieldsJson);
    const errorRows = buildErrorRows(document.errorCodesJson);

    const renderStructuredSection = (title, section, emptyText) => {
      const columns = section?.headers?.map((header) => ({
        title: header,
        dataIndex: header,
        key: header,
      })) || [];

      return (
        <>
          <div className="api-document-block-title">{title}</div>
          {section?.note ? (
            <Paragraph className="api-document-inline-note">{section.note}</Paragraph>
          ) : null}
          {section?.text ? (
            <pre className="api-document-code">{section.text}</pre>
          ) : null}
          {section?.rows?.length > 0 && columns.length > 0 ? (
            <Table
              className="api-document-table"
              columns={columns}
              dataSource={section.rows}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          ) : !section?.note && !section?.text ? (
            <Paragraph type="secondary">{emptyText}</Paragraph>
          ) : null}
        </>
      );
    };

    return (
      <div className="api-document-entry" key={document.id} id={`api-document-${document.id}`}>
        <div className="api-document-entry-head">
          <div>
            <h4>{document.name}</h4>
            <div className="api-document-path">{document.path}</div>
          </div>
          <Tag color={METHOD_COLORS[document.httpMethod] || 'default'}>{document.httpMethod}</Tag>
        </div>

        {document.description && (
          <div className="api-document-description">
            <div className="api-document-description-head">
              <Text strong>Description: </Text>
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyText(document.description, 'Description')}
              >
                Copy
              </Button>
            </div>
            <span>{document.description}</span>
          </div>
        )}

        {document.authType && (
          <div className="api-document-meta">
            <Text strong>Authentication: </Text>
            <span>{document.authType}</span>
          </div>
        )}

        {renderStructuredSection(
          'Request Parameters',
          requestParameters,
          'No request parameter description has been recorded yet.'
        )}

        {document.requestExample && (
          <>
            <div className="api-document-block-title">Request Example</div>
            <pre className="api-document-code">{document.requestExample}</pre>
          </>
        )}

        {renderStructuredSection(
          'Return Field',
          returnFields,
          'No return field description has been recorded yet.'
        )}

        {document.responseExample && (
          <>
            <div className="api-document-block-title">Response Example</div>
            <pre className="api-document-code">{document.responseExample}</pre>
          </>
        )}

        <div className="api-document-block-title">Error Codes</div>
        {errorRows.length > 0 ? (
          <Table
            className="api-document-table"
            columns={errorColumns}
            dataSource={errorRows}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        ) : (
          <Paragraph type="secondary">No error code description has been recorded yet.</Paragraph>
        )}

      </div>
    );
  };

  const renderAuthGuide = () => {
    const clientId = generatedCredentials?.clientId || authConfig?.clientId || 'REPLACE_WITH_YOUR_CLIENT_ID';
    const clientSecret = generatedCredentials?.clientSecret || 'REPLACE_WITH_YOUR_CLIENT_SECRET';
    const expiresIn = EXTERNAL_API_TOKEN_TTL_SECONDS;
    const tokenRequestExample = `curl -X POST {{API_BASE_URL}}${TOKEN_ENDPOINT_PATH} \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=client_credentials" \\
  -d "client_id=${clientId}" \\
  -d "client_secret=${clientSecret}"`;
    const useTokenExample = `curl -X GET {{API_BASE_URL}}/users/list \\
  -H "Authorization: Bearer REPLACE_WITH_YOUR_ACCESS_TOKEN"`;

    return (
      <div className="api-document-auth-guide">
        {/* <section className="api-document-guide-section api-document-system-overview" id="api-system-overview">
          <Title level={1}>System API Documentation</Title>
          <ol className="api-document-step-list">
            <li>
              Log in to the portal with a <Text strong>Partner Admin</Text> account.
            </li>
            <li>
              Open <Text code>Account Settings &gt; API Access</Text> and generate your API credentials.
            </li>
            <li>
              Call <Text code>{TOKEN_ENDPOINT_PATH}</Text> to exchange credentials for an access token.
            </li>
            <li>
              Use the returned token in <Text code>Authorization: Bearer {'<token>'}</Text> for subsequent
              System API calls.
            </li>
          </ol>
        </section> */}

        <section className="api-document-guide-section" id="api-system-urls">
          <Title level={2}>System URLs</Title>
          <Paragraph>
            Replace <Text code>{'{{API_BASE_URL}}'}</Text> in the examples below with the appropriate environment
            URL.
          </Paragraph>
          <div className="api-document-system-url-list">
            {SYSTEM_URLS.map((item) => (
              <div className="api-document-system-url-item" key={item.key}>
                <Text strong>{item.label}</Text>
                <a href={item.url} target="_blank" rel="noreferrer">
                  {item.url}
                </a>
              </div>
            ))}
          </div>
        </section>

        <section className="api-document-guide-section" id="api-auth-guide">
          <Title level={2}>Authentication</Title>
          <Paragraph>
            External companies authenticate with <Text strong>Client ID And Client Secret</Text>. Use the generated
            credentials to exchange for an access token, then call the business APIs with
            <Text code> Authorization: Bearer {'<token>'}</Text>.
          </Paragraph>
          <Paragraph>
            Client ID and Client Secret are generated by Scams.Report for each company. Open the
            <Text strong> Company API Credentials</Text> section and click <Text strong>Generate</Text> or
            <Text strong> Regenerate</Text>. The Client ID remains available after generation, while the Client
            Secret is shown only once because only its encrypted hash is stored. Save the Client Secret securely
            before closing the dialog. If it is lost or may have been exposed, regenerate the credentials.
          </Paragraph>
          <Paragraph>
            Token endpoint:
            <Text code> {TOKEN_ENDPOINT_PATH}</Text>
          </Paragraph>
          <pre className="api-document-code">{tokenRequestExample}</pre>
        </section>

        <section className="api-document-guide-section" id="api-token-response">
          <Title level={2}>Token Response</Title>
          <pre className="api-document-code">{`{
  "access_token": "REPLACE_WITH_YOUR_ACCESS_TOKEN",
  "token_type": "Bearer",
  "expires_in": ${expiresIn}
}`}</pre>
        </section>

        <section className="api-document-guide-section" id="api-use-token">
          <Title level={2}>Call API with the access token</Title>
          <pre className="api-document-code">{useTokenExample}</pre>
        </section>

        <section className="api-document-guide-section" id="api-token-lifetime">
          <Title level={2}>Token Lifetime & Auto-Refresh</Title>
          <Paragraph>
            Each access token has a validity period of <Text strong>{EXTERNAL_API_TOKEN_TTL_SECONDS / 3600} hours</Text>. The
            server will automatically extend the token's lifetime on every authenticated API request, so the token
            remains valid as long as it is used actively.
          </Paragraph>
          <Paragraph>
            If no API call is made within {EXTERNAL_API_TOKEN_TTL_SECONDS / 3600} consecutive hours, the
            token will expire. You must request a new access token via
            <Text code> {TOKEN_ENDPOINT_PATH}</Text> at that point.
          </Paragraph>
          <Paragraph>
            <Text strong>Recommendation:</Text> Obtain a new access token whenever you receive a
            <Text code> 401 Unauthorized</Text> response, rather than relying solely on the
            <Text code> expires_in</Text> value.
          </Paragraph>
        </section>

        <Alert
          className="api-document-auth-alert"
          type="info"
          showIcon
          message={`Token validity period: ${EXTERNAL_API_TOKEN_TTL_SECONDS}s (${EXTERNAL_API_TOKEN_TTL_SECONDS / 3600} hours). The token is automatically extended on each API call, so it remains valid as long as you use it. Regenerate credentials if you suspect secret leakage.`}
        />

        {currentCompanyId ? (
          <Card
            className="api-document-credential-card"
            loading={authLoading}
            title="Company API Credentials"
            extra={
              <Button type="primary" icon={<ReloadOutlined />} onClick={handleRegenerateAuthConfig}>
                {authConfig?.hasClientSecret ? 'Regenerate' : 'Generate'}
              </Button>
            }
            id="api-auth-credentials"
          >
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Company">{authConfig?.companyName || '-'}</Descriptions.Item>
              <Descriptions.Item label="Client ID">
                <Space>
                  <Text code>{authConfig?.clientId || 'Not generated yet'}</Text>
                  {authConfig?.clientId ? (
                    <Button
                      type="link"
                      icon={<CopyOutlined />}
                      onClick={() => copyText(authConfig.clientId, 'Client ID')}
                    >
                      Copy
                    </Button>
                  ) : null}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {authConfig?.externalApiEnabled ? (
                  <Tag color="green">Enabled</Tag>
                ) : (
                  <Tag color="default">Not generated</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {safeFormatTime(authConfig?.credentialsUpdatedAt)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ) : null}
      </div>
    );
  };

  const managementTab = (
    <div className="api-document-management-panel">
      <div className="api-document-management-head">
        <div>
          <Title level={3}>API Documents</Title>
          <Text type="secondary">Create, update, and version the API documentation records.</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchManagementList(filters, pagination)}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            New API
          </Button>
        </Space>
      </div>

      <Form
        form={searchForm}
        className="api-document-management-filter"
        layout="vertical"
        onFinish={handleSearch}
      >
        <Row gutter={16}>
          <Col xs={24} md={6}>
            <Form.Item name="name" label="API Name">
              <Input placeholder="Search by API name" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} md={5}>
            <Form.Item name="version" label="Version">
              <Select
                allowClear
                placeholder="All versions"
                options={versions.map((version) => ({ label: version, value: version }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={5}>
            <Form.Item name="httpMethod" label="Method">
              <Select
                allowClear
                placeholder="All methods"
                options={['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => ({
                  label: method,
                  value: method,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={5}>
            <Form.Item name="path" label="Path">
              <Input placeholder="/client/conversations/list" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} md={3}>
            <Form.Item label=" ">
              <Space>
                <Button type="primary" htmlType="submit">Search</Button>
                <Button onClick={handleReset}>Reset</Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        className="api-document-management-table"
        rowKey="id"
        columns={managementColumns}
        dataSource={documents.rows}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: documents.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `${total} in total`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );

  const previewSidebar = (
    <div className="api-document-preview-sidebar">
      <h3>References</h3>
      <div className="api-document-version-card">
        <p>System Version</p>
        <Select
          value={selectedVersion}
          onChange={setSelectedVersion}
          placeholder="Select system version"
          style={{ width: '80%' }}
          options={systemPublicVersions.map((version) => ({ label: version, value: version }))}
        />
        <div className="api-document-version-updated">
          <span>Document updated</span>
          <strong>{documentUpdatedAt}</strong>
        </div>
      </div>
      <div className="api-document-nav-section">
        <div className="api-document-nav-title">Getting Started</div>
        <div className="api-document-nav-list">
          {visibleSystemReferenceItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`api-document-nav-item${activeNavId === item.id ? ' api-document-nav-item-active' : ''}`}
              onClick={() => {
                setActiveNavId(item.id);
                const element = window.document.getElementById(item.id);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="api-document-nav-section">
        <div className="api-document-nav-title">Endpoints</div>
        <div className="api-document-nav-list">
          {visibleEndpointReferenceItems.map((document) => {
            const navId = `api-document-${document.id}`;
            return (
              <button
                key={document.id}
                type="button"
                className={`api-document-nav-item${activeNavId === navId ? ' api-document-nav-item-active' : ''}`}
                onClick={() => {
                  setActiveNavId(navId);
                  const element = window.document.getElementById(navId);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                {document.name}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const previewTab = (
    <div className="api-document-doc-page">
      <header className="api-document-doc-topbar">
        <div className="api-document-doc-brand">
          <img src="/logo.png" alt="Scams Report" />
          <span>Scams Report</span>
          <strong>Documents</strong>
        </div>
        <div className="api-document-doc-tools">
          <Input
            className="api-document-doc-search"
            value={docSearchTerm}
            onChange={(event) => setDocSearchTerm(event.target.value)}
            prefix={<SearchOutlined />}
            placeholder="Search Keyword"
            allowClear
          />
        </div>
      </header>

      <div className="api-document-preview-shell">
        <div className="api-document-preview-sidebar-column">
          {previewSidebar}
        </div>

        <main className="api-document-preview-content">
          <div className="api-document-module-card">
            {renderAuthGuide()}
          </div>

          {previewLoading ? (
            <Card loading bordered={false} />
          ) : previewRows.length === 0 ? (
            <div className="api-document-preview-empty">
              <div>
                <Title level={4}>No API documents available</Title>
                <Text type="secondary">
                  Create a document record first, then switch back here to preview the rendered result.
                </Text>
              </div>
            </div>
          ) : (
            <div className="api-document-module-card">
              {previewRows.map(renderPreviewDocument)}
            </div>
          )}

          <footer className="api-document-footer">
            Copyright © 2024 | NothingPhishy
          </footer>
        </main>
      </div>
    </div>
  );

  return (
    <div className="api-document-center">
      {isCompanyUser ? previewTab : managementTab}

      <Modal
        title={editingRecord ? 'Edit API Document' : 'New API Document'}
        open={editorOpen}
        onCancel={closeEditor}
        onOk={handleSave}
        confirmLoading={editorLoading}
        width={900}
        okText={editingRecord ? 'Save' : 'Create'}
      >
        <Form
          form={documentForm}
          layout="vertical"
          initialValues={DEFAULT_FORM_VALUES}
        >
          <Row gutter={16}>
            <Col xs={24} md={16}>
              <Form.Item
                name="name"
                label="Document Name"
                rules={[{ required: true, message: 'Please enter the API document name.' }]}
              >
                <Input placeholder="Query session information" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="version"
                label="Version"
                rules={[{ required: true, message: 'Please enter the version.' }]}
              >
                <Input placeholder="v1" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="httpMethod"
                label="HTTP Method"
                rules={[{ required: true, message: 'Please select the HTTP method.' }]}
              >
                <Select
                  options={['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => ({
                    label: method,
                    value: method,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="authType" label="Authentication">
                <Input placeholder="Bearer Token" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="path"
                label="API Path"
                rules={[{ required: true, message: 'Please enter the API path.' }]}
              >
                <Input placeholder="/client/conversations/list" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="summary" label="Summary">
                <Input placeholder="Short business summary shown in the rendered page" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="description" label="Description">
                <TextArea rows={4} placeholder="Detailed business description for external readers" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="paramsJson"
                label="Parameters JSON"
                extra='Example: {"headers":["Field","Type","Description"],"rows":[{"Field":"pageNum","Type":"number","Description":"Page number"}]}'
              >
                <TextArea rows={8} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="returnFieldsJson"
                label="Return Fields JSON"
                extra='Example: {"note":"None","headers":["Status Code","Description"],"rows":[{"Status Code":"200","Description":"Success"}]}'
              >
                <TextArea rows={8} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="requestExample" label="Request Example">
                <TextArea rows={7} placeholder='{"pageNum":1,"pageSize":10}' />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="responseExample" label="Response Example">
                <TextArea rows={7} placeholder='{"code":200,"msg":"Operation successful"}' />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="errorCodesJson"
                label="Error Codes JSON"
                extra='Example: [{"code":"400","message":"Bad Request","description":"Invalid parameters"}]'
              >
                <TextArea rows={6} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="changeLog" label="Change Log">
                <TextArea rows={4} placeholder="Describe version changes or release notes" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Company API Credentials"
        open={credentialsModalOpen}
        footer={null}
        onCancel={() => setCredentialsModalOpen(false)}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="This client secret is shown only once. Store it securely before closing this dialog."
        />
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Client ID">
            <Space>
              <Text code>{generatedCredentials?.clientId}</Text>
              <Button
                type="link"
                icon={<CopyOutlined />}
                onClick={() => copyText(generatedCredentials?.clientId, 'Client ID')}
              >
                Copy
              </Button>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Client Secret">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text code>{generatedCredentials?.clientSecret}</Text>
              <Button
                type="link"
                icon={<CopyOutlined />}
                onClick={() => copyText(generatedCredentials?.clientSecret, 'Client Secret')}
                style={{ paddingInline: 0 }}
              >
                Copy client secret
              </Button>
            </Space>
          </Descriptions.Item>
        </Descriptions>
      </Modal>
    </div>
  );
};

export default ApiDocumentCenter;
